/**
 * @data-dashboard/shared
 *
 * Types and helpers shared between apps/api and apps/web.
 * Keep this package thin — only put things here that BOTH frontend and backend
 * need to import (DTOs, branded types, validation schemas).
 */

// Branded ID types — prevent accidentally passing the wrong id type at compile time.
// Use these on API boundaries; the DB layer uses raw strings internally.
export type AccountId = string & { readonly __brand: "AccountId" };
export type UserId = string & { readonly __brand: "UserId" };
export type CustomerClusterId = string & { readonly __brand: "CustomerClusterId" };
export type OrderId = string & { readonly __brand: "OrderId" };
export type SkuId = string & { readonly __brand: "SkuId" };

export const asAccountId = (raw: string): AccountId => raw as AccountId;
export const asUserId = (raw: string): UserId => raw as UserId;
export const asCustomerClusterId = (raw: string): CustomerClusterId =>
  raw as CustomerClusterId;
export const asOrderId = (raw: string): OrderId => raw as OrderId;
export const asSkuId = (raw: string): SkuId => raw as SkuId;

// ---------------------------------------------------------------------------
// Client explain-tooltip policy (founder decision 2026-07-12)
// ---------------------------------------------------------------------------

/**
 * Every card's ⓘ tooltip is authored as an array of explain lines, each prefixed
 * "<b>What it is:</b>", "<b>How it's calculated:</b>", "<b>Example…</b>", or
 * "<b>Note:</b>". The founder's call: CLIENT tooltips keep only "What it is" and
 * "Note" — the calculation and worked-example lines are operator/docs reference,
 * not client-facing. This is applied CENTRALLY at render time (the web Explain
 * popover) so it hits every card in one place; the ~100 card definitions keep
 * their full strings (revertible, still the operator reference).
 *
 * These are the line PREFIXES a client tooltip hides. Kept here (shared) so the
 * policy has one testable home the API suite can cover — web has no test runner.
 */
export const CLIENT_HIDDEN_EXPLAIN_PREFIXES = ["<b>How it's calculated:</b>", "<b>Example"] as const;

/**
 * Filter explain lines for a CLIENT tooltip: drop any line that starts with a
 * hidden prefix (calculation / worked example). `unfiltered` (operator/admin
 * surfaces) returns the lines untouched. Pure — no DOM, no React.
 */
export function filterClientExplainLines(lines: string[], unfiltered = false): string[] {
  if (unfiltered) return lines;
  return lines.filter((line) => !CLIENT_HIDDEN_EXPLAIN_PREFIXES.some((prefix) => line.startsWith(prefix)));
}

// ---------------------------------------------------------------------------
// Card catalog + approval policy (founder decision 2026-07-12)
// ---------------------------------------------------------------------------

export * from "./card-catalog.js";

// ---------------------------------------------------------------------------
// Capital-return math (flagship CRV + capital-return estimate)
// ---------------------------------------------------------------------------

export * from "./roic.js";

// ---------------------------------------------------------------------------
// The NEEDS registry — one canonical list of "what we still need from you",
// shared by the server derivation, the card warnings, the page banner and the
// setup checklist so they can never disagree (founder 2026-07-13).
// ---------------------------------------------------------------------------

export * from "./needs.js";
